/**
 * Shared utilities for lap calculations across Charts and Tyre Plots
 */

/**
 * Calculate laps from traverses based on session type
 * @param traverses - Array of traverse values
 * @param isTraverseSession - Whether this is a traverse session (analysis_type >= 1000 && < 2000)
 * @returns Array of lap values
 */
export function calculateLaps(traverses: number[], isTraverseSession: boolean): number[] {
  const laps: number[] = [];
  const numTraverses = traverses.length;

  if (numTraverses === 0) return laps;

  if (isTraverseSession) {
    // For traverse sessions: a lap is a traverse pair
    if (numTraverses % 2 === 0) {
      // Even number: pair them as (0,1), (2,3), (4,5), etc.
      for (let i = 0; i < numTraverses; i += 2) {
        const lapAvg = (traverses[i] + traverses[i + 1]) / 2;
        laps.push(lapAvg);
      }
    } else {
      // Odd number: pair them as (0,1), (2,3), etc., and use last 3 for final average
      for (let i = 0; i < numTraverses - 3; i += 2) {
        const lapAvg = (traverses[i] + traverses[i + 1]) / 2;
        laps.push(lapAvg);
      }
      // Average the last 3
      if (numTraverses >= 3) {
        const lastThreeAvg =
          (traverses[numTraverses - 3] + traverses[numTraverses - 2] + traverses[numTraverses - 1]) / 3;
        laps.push(lastThreeAvg);
      } else if (numTraverses === 1) {
        // If only 1 traverse, just use it
        laps.push(traverses[0]);
      }
    }
  } else {
    // For non-traverse sessions: every traverse is a lap (one-to-one)
    laps.push(...traverses);
  }

  return laps;
}

/**
 * Calculate lap timestamps for time-based plots
 * For each lap (traverse pair), calculate the midpoint timestamp
 * @param traverseTimestamps - Array of traverse timestamps (in milliseconds)
 * @param isTraverseSession - Whether this is a traverse session
 * @returns Array of lap timestamps
 */
export function calculateLapTimestamps(traverseTimestamps: number[], isTraverseSession: boolean): number[] {
  const lapTimestamps: number[] = [];
  const numTraverses = traverseTimestamps.length;

  if (numTraverses === 0) return lapTimestamps;

  if (isTraverseSession) {
    // For traverse sessions: calculate midpoint between paired traverses
    if (numTraverses % 2 === 0) {
      // Even number: pair them as (0,1), (2,3), (4,5), etc.
      for (let i = 0; i < numTraverses; i += 2) {
        const midpoint = (traverseTimestamps[i] + traverseTimestamps[i + 1]) / 2;
        lapTimestamps.push(midpoint);
      }
    } else {
      // Odd number: pair them as (0,1), (2,3), etc., and use midpoint of last 3
      for (let i = 0; i < numTraverses - 3; i += 2) {
        const midpoint = (traverseTimestamps[i] + traverseTimestamps[i + 1]) / 2;
        lapTimestamps.push(midpoint);
      }
      // Midpoint of last 3
      if (numTraverses >= 3) {
        const midpoint = traverseTimestamps[numTraverses - 2]; // Use middle timestamp
        lapTimestamps.push(midpoint);
      } else if (numTraverses === 1) {
        // If only 1 traverse, just use it
        lapTimestamps.push(traverseTimestamps[0]);
      }
    }
  } else {
    // For non-traverse sessions: every traverse timestamp is a lap timestamp (one-to-one)
    lapTimestamps.push(...traverseTimestamps);
  }

  return lapTimestamps;
}
